import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { resolveLoungeUrl } from "@/lib/loungeUrl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, Trash2, Save, Plus, ImageIcon, Video, FileText, ArrowUp, ArrowDown, Layers, Download, BarChart3 } from "lucide-react";

export function MediaLibraryTab({ page = "press" }) {
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nw, setNw] = useState({ section: "", title: "", tags: "", url: "" });
  const [newSection, setNewSection] = useState("");
  const [busy, setBusy] = useState(false);
  const [secBusy, setSecBusy] = useState(false);
  const fileRef = useRef(null);
  const posterRefs = useRef({});

  const sectionNames = sections.map((s) => s.name);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/lounge/media?page=${page}`).then(({ data }) => setItems(data.media || [])).catch(() => setItems([])),
      loadSections(),
    ]).finally(() => setLoading(false));
  };
  const loadSections = () =>
    api.get(`/admin/lounge/media-sections?page=${page}`)
      .then(({ data }) => {
        const secs = data.sections || [];
        setSections(secs);
        setNw((n) => ({ ...n, section: n.section && secs.some((s) => s.name === n.section) ? n.section : (secs[0]?.name || "") }));
      })
      .catch(() => setSections([]));
  useEffect(() => { load(); }, [page]);

  // ---- Sezioni/categorie ----
  const addSection = async () => {
    const name = newSection.trim();
    if (!name) return toast.error("Inserisci un nome per la sezione");
    setSecBusy(true);
    try { await api.post("/admin/lounge/media-sections", { name, page }); setNewSection(""); await loadSections(); toast.success("Sezione aggiunta"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setSecBusy(false); }
  };
  const renameSection = async (s) => {
    const name = (s._name !== undefined ? s._name : s.name).trim();
    if (!name || name === s.name) return;
    try { await api.patch(`/admin/lounge/media-sections/${s.id}`, { name }); await loadSections(); await api.get(`/admin/lounge/media?page=${page}`).then(({ data }) => setItems(data.media || [])); toast.success("Sezione rinominata"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const deleteSection = async (s) => {
    if (!window.confirm(`Eliminare la sezione "${s.name}"? L'operazione è possibile solo se la sezione è vuota.`)) return;
    try { await api.delete(`/admin/lounge/media-sections/${s.id}`); await loadSections(); toast.success("Sezione eliminata"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const moveSection = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    const arr = [...sections];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSections(arr);
    try { await api.post("/admin/lounge/media-sections/reorder", { ids: arr.map((x) => x.id) }); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); loadSections(); }
  };
  const setSecField = (id, v) => setSections((arr) => arr.map((x) => (x.id === id ? { ...x, _name: v } : x)));

  // ---- Media ----
  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("section", nw.section); fd.append("title", nw.title); fd.append("tags", nw.tags); fd.append("page", page); fd.append("file", file);
    try { await api.post("/admin/lounge/media/upload", fd); toast.success("Media caricato"); setNw((n) => ({ section: n.section, title: "", tags: "", url: "" })); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const addByUrl = async () => {
    if (!nw.url.trim()) return toast.error("Inserisci un URL");
    setBusy(true);
    try {
      await api.post("/admin/lounge/media", { section: nw.section, title: nw.title, image_url: nw.url.trim(), page, tags: nw.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Media aggiunto"); setNw((n) => ({ section: n.section, title: "", tags: "", url: "" })); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const patch = async (id, body) => { try { await api.patch(`/admin/lounge/media/${id}`, body); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const save = async (it) => { await patch(it.id, { title: it.title, description: it.description, section: it.section, kind: it.kind, poster_url: it.poster_url, video_url: it.video_url, file_url: it.file_url, filename: it.filename, tags: (it._tags !== undefined ? it._tags : (it.tags || []).join(", ")).split(",").map((t) => t.trim()).filter(Boolean), visible: it.visible }); toast.success("Salvato"); };
  const uploadPoster = async (id, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/admin/lounge/media/${id}/poster`, fd); upd(id, "poster_url", data.poster_url); toast.success("Copertina caricata"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/lounge/media/${id}`); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const upd = (id, k, v) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, [k]: v } : x)));

  if (loading) return <div className="flex items-center gap-2 py-10 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>;

  const totalDownloads = items.reduce((a, x) => a + (x.download_count || 0), 0);
  const topItem = items.reduce((b, x) => ((x.download_count || 0) > (b?.download_count || 0) ? x : b), null);

  return (
    <div className="space-y-6" data-testid="media-library-tab">
      {/* Statistiche download */}
      <section className="rounded-xl border border-border bg-brema-card p-5" data-testid="media-download-stats">
        <div className="mb-3 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-brema-red" />
          <h3 className="text-lg font-semibold">Statistiche download</h3>
        </div>
        <div className="flex flex-wrap items-center gap-8 text-sm">
          <div>
            <div className="text-2xl font-bold text-foreground" data-testid="media-downloads-total">{totalDownloads}</div>
            <div className="text-muted-foreground">Download totali</div>
          </div>
          {topItem && (topItem.download_count || 0) > 0 && (
            <div>
              <div className="text-base font-semibold text-foreground">{topItem.title || "—"} <span className="text-brema-red">({topItem.download_count})</span></div>
              <div className="text-muted-foreground">Contenuto più scaricato</div>
            </div>
          )}
        </div>
      </section>

      {/* Gestione sezioni/categorie */}
      <section className="rounded-xl border border-border bg-brema-card p-5" data-testid="media-sections-panel">
        <div className="mb-3 flex items-center gap-2">
          <Layers className="h-5 w-5 text-brema-red" />
          <h3 className="text-lg font-semibold">Sezioni / Categorie</h3>
        </div>
        <p className="mb-3 text-sm text-muted-foreground">Crea, rinomina, riordina o elimina le categorie della Libreria Media. Rinominando una sezione, i contenuti collegati vengono aggiornati automaticamente.</p>
        <div className="space-y-2">
          {sections.map((s, idx) => (
            <div key={s.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background/40 p-2" data-testid={`media-section-row-${s.id}`}>
              <div className="flex flex-col">
                <Button variant="ghost" size="icon" className="h-5 w-6" disabled={idx === 0} onClick={() => moveSection(idx, -1)} data-testid={`media-section-up-${s.id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-5 w-6" disabled={idx === sections.length - 1} onClick={() => moveSection(idx, 1)} data-testid={`media-section-down-${s.id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
              </div>
              <Input value={s._name !== undefined ? s._name : s.name} onChange={(e) => setSecField(s.id, e.target.value)} className="h-9 max-w-xs flex-1" data-testid={`media-section-name-${s.id}`} />
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground" data-testid={`media-section-count-${s.id}`}>{s.count} contenuti</span>
              <Button variant="outline" size="sm" onClick={() => renameSection(s)} data-testid={`media-section-save-${s.id}`}><Save className="mr-1.5 h-3.5 w-3.5" /> Salva</Button>
              <Button variant="ghost" size="sm" onClick={() => deleteSection(s)} data-testid={`media-section-delete-${s.id}`}><Trash2 className="h-4 w-4 text-brema-red" /></Button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-end gap-2">
          <div className="flex-1"><Label className="mb-1.5 block text-xs">Nuova sezione</Label><Input value={newSection} onChange={(e) => setNewSection(e.target.value)} placeholder="Es. Cataloghi, Lookbook, Cartelle Stampa…" data-testid="media-section-new-name" /></div>
          <Button onClick={addSection} disabled={secBusy} className="bg-brema-red text-white hover:bg-brema-red/90" data-testid="media-section-add-btn"><Plus className="mr-1.5 h-4 w-4" /> Aggiungi</Button>
        </div>
      </section>

      {/* Aggiungi media */}
      <section className="rounded-xl border border-border bg-brema-card p-5">
        <h3 className="mb-3 text-lg font-semibold">Aggiungi contenuto</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div>
            <Label className="mb-1.5 block text-xs">Sezione</Label>
            <Select value={nw.section} onValueChange={(v) => setNw((n) => ({ ...n, section: v }))}>
              <SelectTrigger data-testid="media-new-section"><SelectValue placeholder="Seleziona sezione" /></SelectTrigger>
              <SelectContent>{sectionNames.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="mb-1.5 block text-xs">Titolo</Label><Input value={nw.title} onChange={(e) => setNw((n) => ({ ...n, title: e.target.value }))} data-testid="media-new-title" /></div>
          <div><Label className="mb-1.5 block text-xs">Tag (virgola)</Label><Input value={nw.tags} onChange={(e) => setNw((n) => ({ ...n, tags: e.target.value }))} data-testid="media-new-tags" /></div>
          <div className="flex items-end gap-2">
            <input type="file" accept="image/*,video/*,application/pdf,.pdf" hidden ref={fileRef} onChange={(e) => uploadFile(e.target.files?.[0])} />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy || !nw.section} data-testid="media-upload-btn"><Upload className="mr-1.5 h-4 w-4" /> Carica file (foto/video/PDF)</Button>
          </div>
        </div>
        <div className="mt-3 flex items-end gap-2">
          <div className="flex-1"><Label className="mb-1.5 block text-xs">…oppure URL immagine/video</Label><Input value={nw.url} onChange={(e) => setNw((n) => ({ ...n, url: e.target.value }))} placeholder="https://…" data-testid="media-new-url" /></div>
          <Button onClick={addByUrl} disabled={busy || !nw.section} className="bg-brema-red text-white hover:bg-brema-red/90" data-testid="media-add-url-btn"><Plus className="mr-1.5 h-4 w-4" /> Aggiungi</Button>
        </div>
      </section>

      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} className="rounded-xl border border-border bg-brema-card p-4" data-testid={`media-item-${it.id}`}>
            <div className="flex gap-4">
              <div className="relative h-24 w-32 shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
                {(() => {
                  const thumb = it.kind === "video" ? it.poster_url : it.kind === "document" ? it.poster_url : (it.image_url || it.poster_url);
                  if (thumb) return <img src={resolveLoungeUrl(thumb)} alt={it.title} className="h-full w-full object-cover" />;
                  const Icon = it.kind === "video" ? Video : it.kind === "document" ? FileText : ImageIcon;
                  return <div className="flex h-full items-center justify-center text-muted-foreground"><Icon className="h-6 w-6" /></div>;
                })()}
                {it.kind === "video" && <span className="absolute left-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white flex items-center gap-1"><Video className="h-3 w-3" /> Video</span>}
                {it.kind === "document" && <span className="absolute left-1 top-1 rounded bg-brema-red/90 px-1.5 py-0.5 text-[10px] text-white flex items-center gap-1"><FileText className="h-3 w-3" /> PDF</span>}
                {(it.download_count || 0) > 0 && <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white flex items-center gap-1" data-testid={`media-downloads-${it.id}`} title="Download totali"><Download className="h-3 w-3" /> {it.download_count}</span>}
              </div>
              <div className="flex-1 space-y-2">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <Input value={it.title || ""} onChange={(e) => upd(it.id, "title", e.target.value)} placeholder="Titolo" data-testid={`media-title-${it.id}`} />
                  <Select value={it.section || (sectionNames[0] || "")} onValueChange={(v) => upd(it.id, "section", v)}>
                    <SelectTrigger data-testid={`media-section-${it.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{sectionNames.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={it.kind || "image"} onValueChange={(v) => upd(it.id, "kind", v)}>
                    <SelectTrigger data-testid={`media-kind-${it.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">Immagine</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="document">Documento (PDF)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input value={it.description || ""} onChange={(e) => upd(it.id, "description", e.target.value)} placeholder="Descrizione" data-testid={`media-desc-${it.id}`} />
                <Input value={it._tags !== undefined ? it._tags : (it.tags || []).join(", ")} onChange={(e) => upd(it.id, "_tags", e.target.value)} placeholder="Tag (virgola)" data-testid={`media-tags-${it.id}`} />

                {it.kind === "document" && (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 p-2 text-sm" data-testid={`media-file-row-${it.id}`}>
                    <FileText className="h-4 w-4 text-brema-red" />
                    {it.file_url
                      ? <a href={resolveLoungeUrl(it.file_url)} target="_blank" rel="noopener noreferrer" className="font-medium text-brand underline decoration-dotted" data-testid={`media-file-link-${it.id}`}>{it.filename || "Documento PDF"}</a>
                      : <span className="text-muted-foreground">Nessun file: ricarica un PDF dal riquadro "Aggiungi contenuto"</span>}
                  </div>
                )}

                {(it.kind === "video" || it.kind === "document") && (
                  <div className="rounded-lg border border-border bg-muted/40 p-2" data-testid={`media-poster-row-${it.id}`}>
                    <Label className="mb-1.5 block text-xs">{it.kind === "document" ? "Copertina / anteprima (opzionale)" : "Copertina video (poster)"}</Label>
                    <div className="flex items-center gap-2">
                      <div className="h-12 w-16 shrink-0 overflow-hidden rounded border border-border bg-muted">
                        {it.poster_url ? <img src={resolveLoungeUrl(it.poster_url)} alt="poster" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-muted-foreground"><ImageIcon className="h-4 w-4" /></div>}
                      </div>
                      <Input value={it.poster_url || ""} onChange={(e) => upd(it.id, "poster_url", e.target.value)} placeholder="URL copertina immagine" data-testid={`media-poster-url-${it.id}`} />
                      <input type="file" accept="image/*" hidden ref={(el) => (posterRefs.current[it.id] = el)} onChange={(e) => uploadPoster(it.id, e.target.files?.[0])} />
                      <Button variant="outline" size="sm" onClick={() => posterRefs.current[it.id]?.click()} data-testid={`media-poster-upload-${it.id}`}><Upload className="mr-1.5 h-4 w-4" /> Carica</Button>
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={it.visible !== false} onCheckedChange={(v) => upd(it.id, "visible", !!v)} data-testid={`media-visible-${it.id}`} /> Visibile</label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => save(it)} data-testid={`media-save-${it.id}`}><Save className="mr-1.5 h-4 w-4" /> Salva</Button>
                    <Button variant="ghost" size="sm" onClick={() => del(it.id)} data-testid={`media-delete-${it.id}`}><Trash2 className="h-4 w-4 text-brema-red" /></Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
